import { useCallback, useState } from "react";
import contactHttpClient from "utils/contactHttpClient";
import Acquirer from "enums/Acquirer";
import { HttpStatus } from "enums/HttpStatus";
import {
  SubscriptionChargeStatus,
  SubscriptionStatus,
} from "enums/Subscription";

const manageClient = contactHttpClient;

const PAYER_ONLY = { suppressAuthToast: true };

export interface ManagedSubscription {
  status: SubscriptionStatus;
  planName?: string;
  amount: number;
  currency: string;
  interval: string;
  nextBillingDate?: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt?: number | null;
  paymentFailed?: boolean;
  lastDeclineCode?: string | null;
  awaitingPaymentMethod: boolean;
  paymentMethodBrand?: string;
  paymentMethodLast4?: string;
  payerEmail: string;
  startedAt?: number | null;
}

export interface ManagedCharge {
  period: string;
  attempt: number;
  status: SubscriptionChargeStatus;
  declineCode?: string | null;
  amount: number;
  createdAt?: number | null;
}

export interface ManagedCompanyDetails {
  companyName?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyWebsite?: string;
}

export interface ManagedSetupSession {
  savedPaymentMethods?: Array<{
    id: string;
    methodType: string;
    brand?: string;
    last4?: string;
    current: boolean;
  }>;
  clientSecret: string;
  acquirer: {
    acquirer: Acquirer;
    publishableKey: string;
    componentType: string;
  };
}

export interface StartUpdateResult {
  session: ManagedSetupSession | null;
  signInRequired: boolean;
}

export function useManagedSubscription(token?: string) {
  const [subscription, setSubscription] = useState<ManagedSubscription | null>(
    null,
  );
  const [companyDetails, setCompanyDetails] =
    useState<ManagedCompanyDetails | null>(null);
  const [charges, setCharges] = useState<ManagedCharge[]>([]);
  const [setupSession, setSetupSession] = useState<ManagedSetupSession | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidToken, setInvalidToken] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const { data } = await manageClient.get(`/manage-subscription/${token}`);
      setSubscription(data.subscription);
      setCompanyDetails(data.companyDetails);
      setCharges(data.charges || []);
      setError(null);
    } catch (requestError) {
      if (statusOf(requestError) === HttpStatus.NOT_FOUND) {
        setInvalidToken(true);
      }
      setError(messageOf(requestError) || "This link is no longer valid");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const startPaymentMethodUpdate =
    useCallback(async (): Promise<StartUpdateResult> => {
      if (!token) return { session: null, signInRequired: false };

      setLoading(true);
      try {
        const { data } = await manageClient.post<ManagedSetupSession>(
          `/manage-subscription/${token}/setup-intent`,
          undefined,
          PAYER_ONLY,
        );
        setSetupSession(data);
        setError(null);
        return { session: data, signInRequired: false };
      } catch (requestError) {
        const signInRequired = statusOf(requestError) === HttpStatus.FORBIDDEN;
        setError(
          signInRequired
            ? null
            : messageOf(requestError) || "Could not start the update",
        );
        return { session: null, signInRequired };
      } finally {
        setLoading(false);
      }
    }, [token]);

  const savePaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      if (!token) return false;

      setLoading(true);
      try {
        const { data } = await manageClient.post(
          `/manage-subscription/${token}/payment-method`,
          { paymentMethodId },
          PAYER_ONLY,
        );
        setSubscription(data.subscription);
        setSetupSession(null);
        setError(null);
        return true;
      } catch (requestError) {
        setError(
          messageOf(requestError) || "Could not save that payment method",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const cancel = useCallback(
    async (atPeriodEnd: boolean = false) => {
      if (!token) return false;

      setLoading(true);
      try {
        const { data } = await manageClient.post(
          `/manage-subscription/${token}/cancel`,
          {
            atPeriodEnd,
          },
        );
        setSubscription(data.subscription);
        setError(null);
        return true;
      } catch (requestError) {
        setError(
          messageOf(requestError) || "Could not cancel this subscription",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  return {
    subscription,
    companyDetails,
    charges,
    setupSession,
    loading,
    error,
    invalidToken,
    load,
    startPaymentMethodUpdate,
    savePaymentMethod,
    cancel,
  };
}

function messageOf(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}
